// views/Dashboard/PainelExecutivo/index.tsx
//
// Casca do Painel Executivo: barra de filtros, abas por área e o conteúdo da
// área aberta. Cada área é uma rota (`/dashboard/painel-executivo/financeiro`),
// e só a área aberta carrega dados — antes as quatro seções carregavam juntas,
// inclusive as duas mais pesadas (todas as faturas e a carteira de recebíveis).
//
// O estado dos filtros vive AQUI, não nas áreas: trocar de aba não perde o
// período escolhido, porque a casca continua montada enquanto a rota interna muda.
import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Download, Loader2 } from 'lucide-react';
import FiltrosGlobaisDashboard from './components/FiltrosGlobaisDashboard';
import AbasAreas from './components/AbasAreas';
import SeletorBaseData from './components/SeletorBaseData';
import { AREAS, AREAS_COM_FILTRO_PROPRIO, CAMINHO_PAINEL, FILTROS_DASHBOARD_INICIAIS, ehAreaPainel } from './types';
import type { AreaPainel } from './types';
import { exportarAreaPainel } from './exportarPainelExecutivo';
import { useToast } from '../../../src/contexts/ToastContext';
import SecaoGeradoras from './sections/geradoras/SecaoGeradoras';
import SecaoMarketing from './sections/marketing/SecaoMarketing';
import SecaoComercial from './sections/comercial/SecaoComercial';
import AreaFinanceiro from './areas/AreaFinanceiro';
import VisaoGeral from './areas/VisaoGeral';
import AreaEquipes from './areas/AreaEquipes';
import AreaCeo from './areas/AreaCeo';
import AreaMetas from './areas/AreaMetas';

const PainelExecutivoDashboard: React.FC = () => {
  const [filtros, setFiltros] = useState(FILTROS_DASHBOARD_INICIAIS);
  const [exportando, setExportando] = useState(false);
  const { addToast } = useToast();

  // A área sai do que vem DEPOIS de /painel-executivo/ na URL. Segmento
  // desconhecido (link velho, endereço digitado errado) cai na Visão geral, que
  // é a porta de entrada do painel.
  const { pathname } = useLocation();
  const segmento = pathname.split('/painel-executivo/')[1]?.split('/')[0] ?? '';
  const area: AreaPainel = ehAreaPainel(segmento) ? segmento : 'visao-geral';
  const areaAtual = AREAS.find((a) => a.id === area)!;
  const filtroProprio = AREAS_COM_FILTRO_PROPRIO.includes(area);

  const handleExportar = async () => {
    if (area === 'visao-geral' || area === 'equipes' || area === 'ceo' || area === 'metas') return;
    setExportando(true);
    try {
      await exportarAreaPainel(area, {
        periodo: filtros.periodo,
        departamentos: filtros.departamentos,
        baseDataFinanceiro: filtros.baseDataFinanceiro,
        contagemComercial: filtros.contagemComercial,
        horizonteMesesProjecao: 3,
      });
    } catch (e: any) {
      console.error('Erro ao exportar o Painel Executivo:', e);
      addToast(e?.message || 'Não foi possível gerar o Excel.', 'error');
    } finally {
      setExportando(false);
    }
  };

  // Os filtros da área aberta entram na mesma barra — ver FiltrosGlobaisDashboard.
  // O Comercial monta o próprio seletor de canal dentro da seção — ele mostra o
  // kWh de cada canal, e esse dado só existe lá.
  const filtrosDaArea =
    area === 'financeiro' ? (
      <SeletorBaseData
        valor={filtros.baseDataFinanceiro}
        onChange={(baseDataFinanceiro) => setFiltros({ ...filtros, baseDataFinanceiro })}
      />
    ) : null;

  // Atenção: `animate-fade-in` (index.html) roda uma animação com
  // `transform: translateY(...)` e `forwards` — o transform residual em
  // 100% (mesmo sendo translateY(0)) quebra `position: sticky` em qualquer
  // descendente, porque um transform no ancestral cria um novo containing
  // block. Por isso a classe vai só no conteúdo ABAIXO da barra de filtros,
  // nunca em um ancestral de <FiltrosGlobaisDashboard>.
  return (
    <div className="max-w-[1600px] mx-auto px-4 py-5 space-y-4 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-bold text-gray-800 dark:text-white">Painel Executivo</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{areaAtual.descricao}</p>
        </div>
        {area !== 'visao-geral' && !filtroProprio && (
          <button
            type="button"
            onClick={handleExportar}
            disabled={exportando}
            className="sm:ml-auto w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60"
          >
            {exportando ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {exportando ? 'Exportando...' : `Exportar ${areaAtual.label}`}
          </button>
        )}
      </div>

      <AbasAreas />

      {/* Área com período e exportação próprios não mostra a barra do topo: um
          filtro que não muda nada na tela só confunde. */}
      {!filtroProprio && (
        <FiltrosGlobaisDashboard filtros={filtros} onChange={setFiltros} filtrosDaArea={filtrosDaArea} />
      )}

      <Routes>
        <Route index element={<VisaoGeral filtros={filtros} />} />
        <Route path="comercial" element={<SecaoComercial filtros={filtros} onChange={setFiltros} />} />
        <Route path="financeiro" element={<AreaFinanceiro filtros={filtros} />} />
        <Route path="marketing" element={<SecaoMarketing filtros={filtros} />} />
        <Route path="geradoras" element={<SecaoGeradoras filtros={filtros} />} />
        <Route path="equipes" element={<AreaEquipes />} />
        <Route path="ceo" element={<AreaCeo />} />
        <Route path="metas" element={<AreaMetas />} />
        {/* Endereço antigo, digitado errado ou empilhado por um link relativo
            volta para a porta de entrada. Absoluto: um destino relativo aqui
            empilharia mais um segmento em vez de voltar. */}
        <Route path="*" element={<Navigate to={CAMINHO_PAINEL} replace />} />
      </Routes>
    </div>
  );
};

export default PainelExecutivoDashboard;
